import { useCallback, useEffect, useState } from "react";
import { router } from "@inertiajs/react";

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay);
  }) as T;
}

// Debounced router visit with 300ms delay
const debouncedRouterVisit = debounce((path: string, options: any) => {
  router.visit(path, options);
}, 300);

interface CurrentBibleLocation {
  translationSlug: string;
  bookSlug: string;
  chapterNumber: number | string;
}

interface NavigateBibleParams {
  translationSlug?: string;
  bookSlug?: string;
  chapterNumber?: number | string;
  verseNumber?: number | string;
  clearQuery?: boolean;
  query?: string;
  testament?: 'all' | 'new' | 'old';
  sortOrder?: 'traditional' | 'alphabetical';
  onComplete?: () => void;
}

export function getBibleLinkProps(current: CurrentBibleLocation, params: NavigateBibleParams): {
  href: string;
  preserveState: boolean;
  only: string[];
} {
  const { translationSlug, bookSlug, chapterNumber } = current

  const {
    translationSlug: newTranslationSlug,
    bookSlug: newBookSlug,
    chapterNumber: newChapterNumber,
    verseNumber,
    clearQuery = false,
    query,
    testament,
    sortOrder,
  } = params

  const newPath = `/bible/${newTranslationSlug || translationSlug}/${newBookSlug || bookSlug}/${newChapterNumber || chapterNumber}`

  const currentUrl = new URL(window.location.href);
  if (clearQuery) {
    currentUrl.searchParams.delete('query');
  } else if (query !== undefined) {
    query ? currentUrl.searchParams.set('query', query) : currentUrl.searchParams.delete('query');
  }
  if (testament !== undefined) {
    testament && testament !== 'all' ? currentUrl.searchParams.set('testament', testament) : currentUrl.searchParams.delete('testament');
  }
  if (sortOrder !== undefined) {
    sortOrder && sortOrder !== 'traditional' ? currentUrl.searchParams.set('sort', sortOrder) : currentUrl.searchParams.delete('sort');
  }

  // The focused verse only stays valid if the book/chapter aren't changing.
  // A pure translation switch should keep pointing at the same verse.
  const sameChapter = (!newBookSlug || newBookSlug === bookSlug) && (!newChapterNumber || newChapterNumber == chapterNumber);
  const targetVerseNumber = verseNumber ?? (sameChapter ? getFocusedVerse() ?? undefined : undefined);
  const hash = targetVerseNumber ? `#verse-${targetVerseNumber}` : '';
  const fullPath = newPath + currentUrl.search + hash;

  const reloadedFields = ['verses']

  if (newTranslationSlug && newTranslationSlug != translationSlug) {
    reloadedFields.push(...['chosen_translation', 'books', 'queried_books', 'chosen_book', 'chosen_chapter', 'queried_verses'])
  }

  if (newBookSlug && newBookSlug != bookSlug) {
    reloadedFields.push(...['chosen_book', 'chosen_chapter'])
  }

  if (newChapterNumber && newChapterNumber != chapterNumber) {
    reloadedFields.push(...['chosen_chapter'])
  }

  if (clearQuery || query !== undefined || testament !== undefined || sortOrder !== undefined) {
    reloadedFields.push(...['queried_books', 'queried_verses'])
  }

  return { href: fullPath, preserveState: true, only: reloadedFields }
}

export const navigateBible = (current: CurrentBibleLocation, params: NavigateBibleParams) => {
  if (typeof window === 'undefined') { return null }

  const { href, preserveState, only } = getBibleLinkProps(current, params)
  debouncedRouterVisit(href, { preserveState, only, onSuccess: params.onComplete })
}

export function getFocusedVerse(): number | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.hash.match(/^#verse-(\d+)$/);
  return match ? parseInt(match[1], 10) : null;
}

// Parses a JSON-encoded string, falling back to `{}` (or a provided default) on
// empty/invalid input - so callers reading a JSON blob out of a URL param don't
// each need their own try/catch.
export function parseJson<T>(raw: string, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

// Fired after syncUrlParam so other useUrlQueryParam instances (e.g. in a
// sibling component) pick up the change - history.replaceState doesn't emit
// popstate, so without this, same-tab siblings never learn the URL moved.
const QUERY_PARAM_CHANGE_EVENT = 'queryparamchange';

// Writes a single query param onto the current URL without navigating (no server
// request, no page reload) - just a history entry so the value survives a refresh
// and responds to back/forward. An empty value deletes the param entirely, so the
// URL matches what a real navigation produces (no `query=` vs. an absent `query`).
function syncUrlParam(key: string, value: string) {
  const url = new URL(window.location.href);
  value ? url.searchParams.set(key, value) : url.searchParams.delete(key);
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  window.dispatchEvent(new Event(QUERY_PARAM_CHANGE_EVENT));
}

// Re-runs `onChange` whenever the URL might have moved: back/forward nav
// (popstate), an Inertia visit (pushState, which doesn't fire popstate on its
// own), or another useUrlQueryParam/useCurrentSearch instance writing a param
// via replaceState (QUERY_PARAM_CHANGE_EVENT - replaceState doesn't fire
// popstate either, so without this, sibling components never learn the URL
// moved). Shared by useUrlQueryParam and useCurrentSearch so both stay in
// sync off the exact same set of triggers.
function useUrlChangeEffect(onChange: () => void) {
  useEffect(() => {
    window.addEventListener('popstate', onChange);
    const unsubscribeInertia = router.on('navigate', onChange);
    window.addEventListener(QUERY_PARAM_CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      unsubscribeInertia();
      window.removeEventListener(QUERY_PARAM_CHANGE_EVENT, onChange);
    };
  }, [onChange]);
}

// Generic client-only state for a single URL query param - read on mount, written
// back via history.replaceState (no server request, no page reload), and re-read
// on back/forward navigation. Defaults to a string param; pass `asNumber: true` to
// read/write a number instead (falls back to `defaultValue` when missing or not a
// valid positive number). Callers still own any further parsing (JSON, etc.).
export function useUrlQueryParam(key: string, defaultValue?: string): [string, (value: string) => void];
export function useUrlQueryParam(key: string, defaultValue: number, asNumber: true): [number, (value: number) => void];
export function useUrlQueryParam(
  key: string,
  defaultValue: string | number = '',
  asNumber?: boolean
): [string | number, (value: any) => void] {
  const read = useCallback(
    () => (typeof window === 'undefined' ? null : new URL(window.location.href).searchParams.get(key)),
    [key]
  );
  const [raw, setRaw] = useState<string | null>(read);

  useUrlChangeEffect(useCallback(() => setRaw(read()), [read]));

  const setValue = useCallback((next: string | number) => {
    const nextRaw = String(next);
    setRaw(current => {
      if (nextRaw === current) return current;
      syncUrlParam(key, nextRaw);
      return nextRaw;
    });
  }, [key]);

  if (asNumber) {
    const parsed = parseInt(raw ?? '', 10);
    const value = Number.isFinite(parsed) && parsed > 0 ? parsed : (defaultValue as number);
    return [value, setValue];
  }

  return [raw ?? (defaultValue as string), setValue];
}

// The current URL's full query string (e.g. "?appearance=...&fontSize=20"),
// kept live via the same events useUrlQueryParam listens to. For building a
// link that should carry forward whatever params happen to be present -
// reads the URL as-is instead of naming/re-encoding each param by hand.
export function useCurrentSearch(): string {
  const [search, setSearch] = useState(
    () => (typeof window === 'undefined' ? '' : window.location.search)
  );

  useUrlChangeEffect(useCallback(() => setSearch(window.location.search), []));

  return search;
}

// Client-only state for the focused verse (`#verse-N`), read from and written to
// the URL hash rather than a query param - kept separate from useUrlQueryParam
// since the hash has its own change event and its own navigation semantics
// (pushState, so back/forward steps through previously focused verses).
export function useFocusedVerse(): [number | null, (verseNumber: number) => void] {
  const [focusedVerse, setFocusedVerseState] = useState<number | null>(getFocusedVerse);

  useEffect(() => {
    const onHashChange = () => setFocusedVerseState(getFocusedVerse());
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('popstate', onHashChange);
    // Inertia navigations (e.g. clicking a <Link> to a search result) update
    // window.location.hash via history.pushState, which does NOT fire hashchange
    // or popstate - so without this, the focused verse silently stops updating
    // after any in-app navigation.
    const unsubscribeInertia = router.on('navigate', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('popstate', onHashChange);
      unsubscribeInertia();
    };
  }, []);

  const setFocusedVerse = useCallback((verseNumber: number) => {
    setFocusedVerseState(current => {
      if (verseNumber === current) return current;
      const url = new URL(window.location.href);
      url.hash = `verse-${verseNumber}`;
      window.history.pushState(null, '', url.pathname + url.search + url.hash);
      return verseNumber;
    });
  }, []);

  return [focusedVerse, setFocusedVerse];
}
